import dayjs from 'dayjs'
import { IExerciseRecords } from 'hooks/useExerciseRecords'
import { FC, useMemo, useState } from 'react'
import { LuCalendarCheck, LuClock, LuDumbbell, LuRepeat, LuTrophy, LuUsers } from 'react-icons/lu'
import { BarChart } from 'components/BarChart'
import { LineChart } from 'components/LineChart'
import { ChartBox, Section, SectionHeading } from 'components/ui/Section'
import { Segmented } from 'components/ui/Segmented'
import { ChartSkeleton, EmptyState } from 'components/ui/States'
import { filterByMonth, formatFixed, formatInt, getMostCommonExercise, groupByMonth, percentChange, summarize } from 'utils/stats'
import { getExerciseName } from 'variables/excersise'
import { RecordCardView } from './recordCardView'

interface IThisMonthSection {
    data: IExerciseRecords[]
    loading: boolean
    windowStart: Date
}

type ITrendMetric = 'minutes' | 'sessions'

export const ThisMonthSection: FC<IThisMonthSection> = ({ data, loading, windowStart }) => {
    const [trendMetric, setTrendMetric] = useState<ITrendMetric>('minutes')

    const { tm, lm, mostCommon } = useMemo(() => {
        const now = dayjs()
        const thisMonth = filterByMonth(data, now)
        const lastMonth = filterByMonth(data, now.subtract(1, 'month'))
        return { tm: summarize(thisMonth), lm: summarize(lastMonth), mostCommon: getMostCommonExercise(thisMonth) }
    }, [data])

    const monthly = useMemo(() => groupByMonth(data, dayjs(windowStart), dayjs()), [data, windowStart])
    const monthCategories = useMemo(() => monthly.map((m) => dayjs(`${m.month}-01`).format('M月')), [monthly])
    const monthTooltips = useMemo(() => monthly.map((m) => dayjs(`${m.month}-01`).format('YYYY年M月')), [monthly])
    const windowLabel = monthly.length ? `${monthTooltips[0]} – ${monthTooltips[monthTooltips.length - 1]}` : ''

    const tmMinutes = Math.round(tm.totalSeconds / 60)

    return <div className='space-y-4 lg:space-y-6'>
        <SectionHeading title='本月概況' description={`${dayjs().format('YYYY年M月')} · 與上月比較`} />
        <div className='grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6'>
            <RecordCardView
                title='本月運動總時數'
                icon={<LuClock />}
                value={formatInt(tmMinutes)}
                unit='分鐘'
                description={tmMinutes >= 60 ? `約 ${formatFixed(tmMinutes / 60, 1)} 小時` : undefined}
                showDelta
                percentage={percentChange(tm.totalSeconds, lm.totalSeconds)}
                decimals={2}
                loading={loading} />
            <RecordCardView
                title='本月運動次數'
                icon={<LuDumbbell />}
                value={formatInt(tm.sessions)}
                unit='次'
                showDelta
                percentage={percentChange(tm.sessions, lm.sessions)}
                loading={loading} />
            <RecordCardView
                title='本月總完成次數'
                icon={<LuRepeat />}
                value={formatInt(tm.totalReps)}
                unit='下'
                showDelta
                percentage={percentChange(tm.totalReps, lm.totalReps)}
                loading={loading} />
            <RecordCardView
                title='本月參與學生'
                icon={<LuUsers />}
                value={formatInt(tm.students)}
                unit='人'
                showDelta
                percentage={percentChange(tm.students, lm.students)}
                loading={loading} />
            <RecordCardView
                title='本月最多人參與的運動'
                icon={<LuTrophy />}
                value={mostCommon.exercise ? getExerciseName(mostCommon.exercise) : '—'}
                textValue
                description={mostCommon.exercise ? `${formatInt(mostCommon.count)} 次` : '本月暫無紀錄'}
                loading={loading} />
            <RecordCardView
                title='本月活躍日數'
                icon={<LuCalendarCheck />}
                value={formatInt(tm.activeDays)}
                unit='日'
                showDelta
                percentage={percentChange(tm.activeDays, lm.activeDays)}
                loading={loading} />
        </div>

        <Section
            title='過去12個月趨勢'
            subtitle={`${trendMetric === 'minutes' ? '每月運動總時數 · 分鐘' : '每月運動次數 · 次'}${windowLabel ? ` · ${windowLabel}` : ''}`}
            actions={<Segmented<ITrendMetric>
                ariaLabel='趨勢指標'
                options={[{ value: 'minutes', label: '時數' }, { value: 'sessions', label: '次數' }]}
                value={trendMetric}
                onChange={setTrendMetric} />}>
            {loading
                ? <ChartSkeleton />
                : data.length === 0
                    ? <EmptyState title='過去12個月沒有運動紀錄' compact />
                    : <ChartBox tall>
                        {trendMetric === 'minutes'
                            ? <LineChart
                                categories={monthCategories}
                                tooltipLabels={monthTooltips}
                                series={[{ name: '運動時數', data: monthly.map((m) => m.minutes) }]}
                                unit='分鐘'
                                yTitle='分鐘' />
                            : <BarChart
                                categories={monthCategories}
                                tooltipLabels={monthTooltips}
                                series={[{ name: '運動次數', data: monthly.map((m) => m.sessions) }]}
                                unit='次'
                                yTitle='次數' />}
                    </ChartBox>}
        </Section>
    </div>
}
